<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import RoundFinishButton from "./RoundFinishButton.vue";

interface Props {
  canStartTournament: boolean;
  isTournamentStarted: boolean;
  isTournamentFinished: boolean;
  hasUncompletedMatches: boolean;
  canAdvance: boolean;
  actionLoading: boolean;
  currentRound: number;
  matchesByRound: Record<number, any[]>;
  isRoundActive: (round: number) => boolean;
  getCompletedMatchesCount: (round: number) => number;
  totalRounds: number;
}

interface Emits {
  (e: 'start-tournament'): void;
  (e: 'randomize-round'): void;
  (e: 'advance-to-next-round'): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
</script>

<template>
  <!-- Tournament Actions -->
  <div class="tournament-actions">
    <button
      v-if="canStartTournament"
      @click="emit('start-tournament')"
      class="btn btn-primary"
      type="button"
      :disabled="actionLoading"
    >
      {{ actionLoading ? t('tournament.actions.starting') : t('tournament.actions.start_tournament') }}
    </button>

    <button
      v-if="isTournamentStarted && !isTournamentFinished && hasUncompletedMatches"
      @click="emit('randomize-round')"
      class="btn btn-secondary"
      type="button"
      :disabled="actionLoading"
      :title="t('tournament.actions.randomize_unfinished_title')"
    >
      {{ actionLoading ? t('tournament.actions.randomizing') : t('tournament.actions.randomize_unfinished') }}
    </button>

    <div class="next-round-container">
      <RoundFinishButton
        v-if="isTournamentStarted && !isTournamentFinished"
        :round="currentRound"
        :matches="matchesByRound[currentRound] || []"
        :action-loading="actionLoading"
        :is-round-active="isRoundActive"
        :get-completed-matches-count="getCompletedMatchesCount"
        :is-last-round="currentRound >= totalRounds"
        @finish-round="emit('advance-to-next-round')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tournament-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  align-items: center;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.btn-primary {
    background-color: #4caf50;
    color: white;

    &:hover:not(:disabled) {
      background-color: #45a049;
    }
  }

  &.btn-secondary {
    background-color: #ff9800;
    color: white;

    &:hover:not(:disabled) {
      background-color: #f57c00;
    }
  }
}

.next-round-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  align-items: flex-start;
}

// Mobile responsive
@media (max-width: 768px) {
  .tournament-actions {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }

  .btn {
    justify-content: center;
    padding: var(--spacing-md) var(--spacing-lg);
  }

  .next-round-container {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }
}

@media (max-width: 480px) {
  .btn {
    padding: 10px 14px;
    font-size: 14px;
  }
}
</style>
